import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Printer, FileText, Trash2, AlertTriangle, PackagePlus } from "lucide-react";
import BdsLogo from "@/components/BdsLogo";
import { inr } from "@/lib/format";

const listeners = new Set();
let config = { enabled: true, intensity: "normal", pos_success: true };

export const setFxConfig = (ui = {}) => {
  config = {
    enabled: ui.animations_enabled !== false,
    intensity: ui.animation_intensity || "normal",
    pos_success: ui.pos_success_animation !== false,
  };
};

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const emit = (e) => {
  if (!config.enabled) return;
  if (e.kind === "bill" && !config.pos_success) return;
  listeners.forEach((l) => l({ ...e, id: Math.random().toString(36).slice(2) }));
};

export const fx = {
  saved: (message) => emit({ kind: "success", message }),
  added: (message) => emit({ kind: "added", message }),
  updated: (message) => emit({ kind: "success", message }),
  removed: (message) => emit({ kind: "removed", message }),
  print: (message = "Print ready") => emit({ kind: "print", message }),
  pdf: (message = "PDF generated successfully") => emit({ kind: "pdf", message }),
  error: (message) => emit({ kind: "error", message }),
  bill: (invoice) => emit({ kind: "bill", invoice }),
};

const TONE = {
  success: { Icon: Check, ring: "ring-emerald-200", bg: "bg-emerald-50", fg: "text-emerald-700" },
  added: { Icon: PackagePlus, ring: "ring-indigo-200", bg: "bg-indigo-50", fg: "text-indigo-700" },
  removed: { Icon: Trash2, ring: "ring-slate-200", bg: "bg-slate-50", fg: "text-slate-700" },
  print: { Icon: Printer, ring: "ring-sky-200", bg: "bg-sky-50", fg: "text-sky-700" },
  pdf: { Icon: FileText, ring: "ring-violet-200", bg: "bg-violet-50", fg: "text-violet-700" },
  error: { Icon: AlertTriangle, ring: "ring-rose-200", bg: "bg-rose-50", fg: "text-rose-700" },
};

const Toastlet = ({ item }) => {
  const flat = reducedMotion();
  const strong = config.intensity === "enhanced" && !flat;
  const { Icon, ring, bg, fg } = TONE[item.kind] || TONE.success;
  return (
    <motion.div
      data-testid={`fx-${item.kind}`}
      initial={flat ? { opacity: 0 } : { opacity: 0, y: 26, rotateX: strong ? -38 : -22, scale: 0.94 }}
      animate={flat ? { opacity: 1 } : { opacity: 1, y: 0, rotateX: 0, scale: 1 }}
      exit={flat ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.96 }}
      transition={flat ? { duration: 0.18 } : { type: "spring", stiffness: 320, damping: 22, mass: 0.6 }}
      style={{ transformPerspective: 700 }}
      className={`pointer-events-none flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.45)] ring-1 ${ring} backdrop-blur`}
    >
      <motion.span
        initial={flat ? {} : { scale: 0.3, rotate: item.kind === "error" ? 0 : -90 }}
        animate={
          flat
            ? {}
            : item.kind === "error"
            ? { scale: 1, x: [0, -5, 5, -3, 0] }
            : { scale: 1, rotate: 0 }
        }
        transition={{ duration: 0.45 }}
        className={`flex h-9 w-9 items-center justify-center rounded-xl ${bg} ${fg}`}
      >
        <Icon className="h-4.5 w-4.5" strokeWidth={2.4} />
      </motion.span>
      <p className="max-w-[260px] text-sm font-semibold text-slate-800">{item.message}</p>
    </motion.div>
  );
};

const BillSuccess = ({ item }) => {
  const flat = reducedMotion();
  const inv = item.invoice || {};
  return (
    <motion.div
      data-testid="fx-bill-success"
      initial={flat ? { opacity: 0 } : { opacity: 0, y: 34, rotateX: -26, scale: 0.9 }}
      animate={flat ? { opacity: 1 } : { opacity: 1, y: 0, rotateX: 0, scale: 1 }}
      exit={flat ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: -14 }}
      transition={flat ? { duration: 0.2 } : { type: "spring", stiffness: 260, damping: 20 }}
      style={{ transformPerspective: 900 }}
      className="pointer-events-none flex flex-col items-center gap-2 rounded-3xl bg-white px-8 py-6 shadow-[0_30px_70px_-24px_rgba(15,23,42,0.55)] ring-1 ring-emerald-200"
    >
      <BdsLogo size={38} showText={false} />
      <motion.span
        initial={flat ? {} : { scale: 0.2 }}
        animate={flat ? {} : { scale: [0.2, 1.12, 1] }}
        transition={{ duration: 0.5 }}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"
      >
        <Check className="h-7 w-7" strokeWidth={3} />
      </motion.span>
      <p className="font-outfit text-base font-bold text-emerald-700">Bill Completed Successfully</p>
      <p className="font-mono text-xs text-slate-500">{inv.invoice_no}</p>
      <p className="font-outfit text-2xl font-bold tnum text-indigo-700">{inr(inv.grand_total)}</p>
    </motion.div>
  );
};

export default function ActionFxHost() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const listener = (e) => {
      setItems((prev) => [...prev, e]);
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== e.id)), e.kind === "bill" ? 1500 : 1900);
    };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const bills = items.filter((i) => i.kind === "bill");
  const toasts = items.filter((i) => i.kind !== "bill").slice(-3);

  return (
    <>
      <div className="pointer-events-none fixed left-1/2 top-20 z-[200] flex -translate-x-1/2 flex-col items-center gap-2 px-3">
        <AnimatePresence>
          {toasts.map((i) => (
            <Toastlet key={i.id} item={i} />
          ))}
        </AnimatePresence>
      </div>
      <div className="pointer-events-none fixed inset-0 z-[210] flex items-center justify-center">
        <AnimatePresence>
          {bills.map((i) => (
            <BillSuccess key={i.id} item={i} />
          ))}
        </AnimatePresence>
      </div>
    </>
  );
}
